/**
 * CheckpointManager - Sistema de checkpoints para recuperação de falhas
 *
 * Salva o estado antes de operações críticas para permitir recuperação
 * em caso de falhas parciais (crash, fechamento do app, etc).
 */

import uuid from 'react-uuid'
import { syncLogger } from './SyncLogger'

const CHECKPOINT_KEY = 'sync_checkpoints'
const MAX_CHECKPOINTS = 10

/**
 * Fases de sync onde checkpoints são criados
 */
export const CheckpointPhase = {
  PUSH_STARTED: 'PUSH_STARTED',
  PUSH_ENTITY_SENT: 'PUSH_ENTITY_SENT',
  PULL_RECEIVED: 'PULL_RECEIVED',
  PULL_APPLYING: 'PULL_APPLYING',
  PULL_COMPLETE: 'PULL_COMPLETE',
}

/**
 * Status de um checkpoint
 */
export const CheckpointStatus = {
  CREATED: 'created',
  RECOVERED: 'recovered',
  EXPIRED: 'expired',
}

class CheckpointManager {
  constructor() {
    this.currentCheckpoint = null
  }

  /**
   * Cria um checkpoint antes de uma operação crítica
   *
   * @param {string} phase - Fase da operação (use CheckpointPhase)
   * @param {Object} data - Dados a serem salvos para recuperação
   * @returns {string} ID do checkpoint
   */
  async createCheckpoint(phase, data) {
    const checkpoint = {
      id: uuid(),
      phase,
      createdAt: Date.now(),
      data,
      status: CheckpointStatus.CREATED,
      recovered: false,
    }

    const checkpoints = this._getCheckpoints()
    checkpoints.push(checkpoint)

    // Manter apenas os últimos MAX_CHECKPOINTS
    while (checkpoints.length > MAX_CHECKPOINTS) {
      checkpoints.shift()
    }

    this._saveCheckpoints(checkpoints)
    this.currentCheckpoint = checkpoint

    syncLogger.log('CHECKPOINT_CREATED', {
      id: checkpoint.id,
      phase,
      dataKeys: Object.keys(data),
    })

    return checkpoint.id
  }

  /**
   * Atualiza um checkpoint existente com novos dados
   *
   * @param {string} checkpointId - ID do checkpoint
   * @param {Object} updates - Dados a serem atualizados
   */
  async updateCheckpoint(checkpointId, updates) {
    const checkpoints = this._getCheckpoints()
    const checkpoint = checkpoints.find((cp) => cp.id === checkpointId)

    if (checkpoint) {
      Object.assign(checkpoint, updates, { updatedAt: Date.now() })
      this._saveCheckpoints(checkpoints)

      syncLogger.log('CHECKPOINT_UPDATED', {
        id: checkpointId,
        updates: Object.keys(updates),
      })
    }

    return checkpoint
  }

  /**
   * Marca checkpoint como completado (operação bem-sucedida)
   *
   * @param {string} checkpointId - ID do checkpoint a completar
   */
  async completeCheckpoint(checkpointId) {
    const checkpoints = this._getCheckpoints()
    const checkpoint = checkpoints.find((cp) => cp.id === checkpointId)

    if (checkpoint) {
      checkpoint.status = CheckpointStatus.RECOVERED
      checkpoint.completedAt = Date.now()
      this._saveCheckpoints(checkpoints)

      syncLogger.log('CHECKPOINT_COMPLETED', {
        id: checkpointId,
        phase: checkpoint.phase,
        duration: checkpoint.completedAt - checkpoint.createdAt,
      })
    }

    if (this.currentCheckpoint?.id === checkpointId) {
      this.currentCheckpoint = null
    }
  }

  /**
   * Verifica se há checkpoints não recuperados (app reiniciou durante sync)
   *
   * @returns {Object|null} Checkpoint pendente ou null
   */
  async getUnrecoveredCheckpoint() {
    const checkpoints = this._getCheckpoints()

    // Procurar checkpoint não recuperado (criado mas não completado)
    const pending = checkpoints.find(
      (cp) =>
        cp.status === CheckpointStatus.CREATED &&
        !cp.recovered &&
        // Considerar apenas checkpoints das últimas 24 horas
        Date.now() - cp.createdAt < 24 * 60 * 60 * 1000,
    )

    if (pending) {
      syncLogger.log('CHECKPOINT_FOUND_UNRECOVERED', {
        id: pending.id,
        phase: pending.phase,
        age: Date.now() - pending.createdAt,
      })
    }

    return pending || null
  }

  /**
   * Tenta recuperar de um checkpoint pendente
   *
   * @param {Function} handlers - Objeto com handlers para cada fase
   * @returns {Object} Resultado da recuperação
   */
  async attemptRecovery(handlers = {}) {
    const checkpoint = await this.getUnrecoveredCheckpoint()

    if (!checkpoint) {
      return { needed: false }
    }

    syncLogger.log('CHECKPOINT_RECOVERY_START', {
      id: checkpoint.id,
      phase: checkpoint.phase,
    })

    let result = { needed: true, success: false, action: null }

    try {
      switch (checkpoint.phase) {
        case CheckpointPhase.PUSH_STARTED:
        case CheckpointPhase.PUSH_ENTITY_SENT:
          result = await this._recoverFromPush(checkpoint, handlers)
          break

        case CheckpointPhase.PULL_RECEIVED:
        case CheckpointPhase.PULL_APPLYING:
          result = await this._recoverFromPull(checkpoint, handlers)
          break

        case CheckpointPhase.PULL_COMPLETE:
          // Pull estava completo - apenas marcar como recuperado
          result = { needed: true, success: true, action: 'already_complete' }
          break

        default:
          result = { needed: true, success: false, action: 'unknown_phase' }
      }

      // Marcar checkpoint como recuperado
      checkpoint.recovered = true
      checkpoint.status = CheckpointStatus.RECOVERED
      checkpoint.recoveredAt = Date.now()
      checkpoint.recoveryResult = result

      const checkpoints = this._getCheckpoints()
      const index = checkpoints.findIndex((cp) => cp.id === checkpoint.id)
      if (index >= 0) {
        checkpoints[index] = checkpoint
        this._saveCheckpoints(checkpoints)
      }

      syncLogger.log('CHECKPOINT_RECOVERY_COMPLETE', {
        id: checkpoint.id,
        result,
      })
    } catch (error) {
      syncLogger.error('CHECKPOINT_RECOVERY_FAILED', error, {
        id: checkpoint.id,
        phase: checkpoint.phase,
      })

      result = { needed: true, success: false, error: error.message }
    }

    return result
  }

  /**
   * Recupera de falha durante push
   */
  async _recoverFromPush(checkpoint, handlers) {
    const { entityType, entryIds, payload } = checkpoint.data

    // Se temos um handler de verificação de servidor, usar
    if (handlers.verifyServerReceived) {
      const serverReceived = await handlers.verifyServerReceived(
        checkpoint.data,
      )

      if (serverReceived) {
        // Servidor recebeu - marcar como synced localmente
        if (handlers.markAsSynced) {
          await handlers.markAsSynced(entityType, entryIds)
        }
        return {
          needed: true,
          success: true,
          action: 'verified_and_marked',
          count: entryIds?.length || 0,
        }
      }
    }

    // Servidor não recebeu ou não podemos verificar - re-enfileirar
    if (handlers.requeue && payload) {
      await handlers.requeue(payload, entryIds, entityType)
      return {
        needed: true,
        success: true,
        action: 'requeued',
        count: entryIds?.length || 0,
      }
    }

    return {
      needed: true,
      success: false,
      action: 'no_handler',
    }
  }

  /**
   * Recupera de falha durante pull
   */
  async _recoverFromPull(checkpoint, handlers) {
    const { userId, lastPulledAt, entitiesProcessed } = checkpoint.data

    // Se pull estava no meio, não atualizar lastPulledAt
    // O próximo sync tentará novamente

    if (checkpoint.phase === CheckpointPhase.PULL_APPLYING) {
      // Estava aplicando mudanças - pode estar em estado inconsistente
      // Forçar re-sync completo se handler disponível
      if (handlers.forceResync) {
        await handlers.forceResync(userId)
        return {
          needed: true,
          success: true,
          action: 'forced_resync',
        }
      }
    }

    // Pull recebido mas não aplicado - tentar aplicar novamente
    if (handlers.retryApply && checkpoint.data.syncObject) {
      try {
        await handlers.retryApply(checkpoint.data.syncObject)
        return {
          needed: true,
          success: true,
          action: 'retry_applied',
        }
      } catch {
        return {
          needed: true,
          success: false,
          action: 'retry_failed',
        }
      }
    }

    return {
      needed: true,
      success: true,
      action: 'will_retry_next_sync',
    }
  }

  /**
   * Limpa checkpoints antigos (mais de 24h)
   */
  cleanupOldCheckpoints() {
    const checkpoints = this._getCheckpoints()
    const cutoff = Date.now() - 24 * 60 * 60 * 1000

    const cleaned = checkpoints.filter((cp) => cp.createdAt > cutoff)

    if (cleaned.length < checkpoints.length) {
      this._saveCheckpoints(cleaned)
      syncLogger.log('CHECKPOINTS_CLEANUP', {
        removed: checkpoints.length - cleaned.length,
        remaining: cleaned.length,
      })
    }
  }

  /**
   * Obtém estatísticas dos checkpoints
   */
  getStats() {
    const checkpoints = this._getCheckpoints()

    return {
      total: checkpoints.length,
      pending: checkpoints.filter(
        (cp) => cp.status === CheckpointStatus.CREATED,
      ).length,
      recovered: checkpoints.filter(
        (cp) => cp.status === CheckpointStatus.RECOVERED,
      ).length,
      oldest: checkpoints.length > 0 ? checkpoints[0].createdAt : null,
      newest:
        checkpoints.length > 0
          ? checkpoints[checkpoints.length - 1].createdAt
          : null,
    }
  }

  /**
   * Limpa todos os checkpoints
   */
  clearAll() {
    this._saveCheckpoints([])
    this.currentCheckpoint = null
    syncLogger.log('CHECKPOINTS_CLEARED', {})
  }

  // Métodos privados de storage

  _getCheckpoints() {
    try {
      const stored = localStorage.getItem(CHECKPOINT_KEY)
      return stored ? JSON.parse(stored) : []
    } catch {
      return []
    }
  }

  _saveCheckpoints(checkpoints) {
    try {
      localStorage.setItem(CHECKPOINT_KEY, JSON.stringify(checkpoints))
    } catch (e) {
      console.error('[CheckpointManager] Failed to save checkpoints:', e)
    }
  }
}

// Singleton
export const checkpointManager = new CheckpointManager()

export default CheckpointManager
